/**
 * OffersPage - Módulo completo de ofertas y campañas
 */

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import {
  Megaphone,
  Plus,
  TrendingUp,
  Users,
  Send,
  BarChart3,
  Eye,
  MousePointerClick,
  CheckCircle2,
  Pencil,
  Trash2,
  Loader2,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';

export function OffersPage() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [offerName, setOfferName] = useState('');
  const [offerType, setOfferType] = useState('');
  const [offerDescription, setOfferDescription] = useState('');
  const [offers, setOffers] = useState([
    {
      id: '1',
      name: 'Descuento Black Friday 2024',
      type: 'discount',
      status: 'draft',
      audience: 'Todos los usuarios',
      stats: { sent: 0, opened: 0, clicked: 0, converted: 0 },
    },
    {
      id: '2',
      name: 'Newsletter Semanal',
      type: 'newsletter',
      status: 'active',
      audience: 'Suscriptores',
      stats: { sent: 1250, opened: 845, clicked: 234, converted: 45 },
    },
    {
      id: '3',
      name: 'Lanzamiento Nuevas Features',
      type: 'announcement',
      status: 'completed',
      audience: 'Usuarios Premium',
      stats: { sent: 456, opened: 398, clicked: 156, converted: 23 },
    },
  ]);
  const [launchingId, setLaunchingId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState<string | null>(null);

  const globalStats = {
    totalOffers: offers.length,
    activeOffers: offers.filter(o => o.status === 'active').length,
    totalSent: offers.reduce((sum, o) => sum + o.stats.sent, 0),
    conversionRate: 4.2,
  };

  const campaigns = [
    {
      id: '1',
      offerName: 'Newsletter Semanal',
      status: 'completed',
      recipients: 1250,
      successful: 1245,
      failed: 5,
      completedAt: '2024-01-15 10:00',
    },
    {
      id: '2',
      offerName: 'Lanzamiento Features',
      status: 'running',
      recipients: 456,
      successful: 420,
      failed: 0,
      completedAt: null,
    },
  ];

  const handleCreateOffer = () => {
    if (!offerName || !offerType || !offerDescription) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    const newOffer = {
      id: String(Date.now()),
      name: offerName,
      type: offerType,
      status: 'draft',
      audience: 'Todos los usuarios',
      stats: { sent: 0, opened: 0, clicked: 0, converted: 0 },
    };

    setOffers(prev => [...prev, newOffer]);
    toast.success(`Oferta "${offerName}" creada correctamente`);
    setIsCreateDialogOpen(false);
    
    // Reset form
    setOfferName('');
    setOfferType('');
    setOfferDescription('');
  };

  const handleLaunchCampaign = async (offerId: string, offerName: string) => {
    setLaunchingId(offerId);
    toast.info(`Lanzando campaña "${offerName}"...`);
    
    // Simular lanzamiento
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setOffers(prev => prev.map(o => 
      o.id === offerId 
        ? { ...o, status: 'active', stats: { ...o.stats, sent: 150 } }
        : o
    ));
    
    setLaunchingId(null);
    toast.success(`Campaña "${offerName}" lanzada exitosamente`);
  };

  const handleEditOffer = (offerId: string) => {
    const offer = offers.find(o => o.id === offerId);
    toast.info(`Editando oferta "${offer?.name}"...`);
  };

  const handleDeleteOffer = () => {
    if (offerToDelete) {
      const offer = offers.find(o => o.id === offerToDelete);
      setOffers(prev => prev.filter(o => o.id !== offerToDelete));
      toast.success(`Oferta "${offer?.name}" eliminada correctamente`);
      setDeleteDialogOpen(false);
      setOfferToDelete(null);
    }
  };

  const openDeleteDialog = (offerId: string) => {
    setOfferToDelete(offerId);
    setDeleteDialogOpen(true);
  };

  const getOfferTypeName = (type: string) => {
    const types: Record<string, string> = {
      discount: 'Descuento',
      promotion: 'Promoción',
      announcement: 'Anuncio',
      newsletter: 'Newsletter',
    };
    return types[type] || type;
  };

  return (
    <div className="space-y-8 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl mb-2">Ofertas y Campañas</h1>
          <p className="text-muted-foreground">
            Gestión de ofertas comerciales y campañas de marketing
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90">
              <Plus className="mr-2 h-4 w-4" />
              Nueva Oferta
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Crear Nueva Oferta</DialogTitle>
              <DialogDescription>
                Define los detalles de tu oferta o campaña
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre de la Oferta *</Label>
                <Input
                  id="name"
                  placeholder="Ej: Descuento Navidad 2024"
                  value={offerName}
                  onChange={(e) => setOfferName(e.target.value)}
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Tipo *</Label>
                <Select value={offerType} onValueChange={setOfferType}>
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Selecciona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discount">Descuento</SelectItem>
                    <SelectItem value="promotion">Promoción</SelectItem>
                    <SelectItem value="announcement">Anuncio</SelectItem>
                    <SelectItem value="newsletter">Newsletter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descripción *</Label>
                <Textarea
                  id="description"
                  placeholder="Describe los detalles de la oferta..."
                  value={offerDescription}
                  onChange={(e) => setOfferDescription(e.target.value)}
                  rows={4}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreateOffer} className="bg-primary hover:bg-primary/90 flex-1">
                  Crear Oferta
                </Button>
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid gap-6 md:grid-cols-4">
        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Ofertas</CardTitle>
            <Megaphone className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold mb-1">{globalStats.totalOffers}</div>
            <p className="text-xs text-muted-foreground">
              {globalStats.activeOffers} activas
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Enviadas</CardTitle>
            <Users className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold mb-1">{globalStats.totalSent.toLocaleString()}</div>
            <p className="text-xs text-green-600">
              <TrendingUp className="inline h-3 w-3 mr-1" />
              +15% vs mes anterior
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Tasa Conversión</CardTitle>
            <BarChart3 className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold mb-1">{globalStats.conversionRate}%</div>
            <p className="text-xs text-muted-foreground">Promedio general</p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Activas</CardTitle>
            <Send className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold mb-1">{globalStats.activeOffers}</div>
            <p className="text-xs text-muted-foreground">Campañas en curso</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="offers" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="offers">Ofertas</TabsTrigger>
          <TabsTrigger value="campaigns">Campañas</TabsTrigger>
        </TabsList>

        {/* Offers Tab */}
        <TabsContent value="offers" className="space-y-4 mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {offers.map((offer) => (
              <Card key={offer.id} className="relative overflow-hidden border-border hover:shadow-lg transition-shadow">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CardTitle className="text-lg">{offer.name}</CardTitle>
                        <Badge
                          variant={
                            offer.status === 'active'
                              ? 'default'
                              : offer.status === 'completed'
                              ? 'secondary'
                              : 'outline'
                          }
                          className="text-xs"
                        >
                          {offer.status === 'active' && 'Activa'}
                          {offer.status === 'draft' && 'Borrador'}
                          {offer.status === 'completed' && 'Completada'}
                        </Badge>
                      </div>
                      <CardDescription>
                        {getOfferTypeName(offer.type)} • {offer.audience}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-accent/50 p-3 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Send className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground font-medium">Enviadas</span>
                        </div>
                        <p className="text-xl font-semibold">{offer.stats.sent}</p>
                      </div>
                      <div className="bg-accent/50 p-3 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Eye className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground font-medium">Abiertas</span>
                        </div>
                        <p className="text-xl font-semibold">{offer.stats.opened}</p>
                      </div>
                      <div className="bg-accent/50 p-3 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <MousePointerClick className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground font-medium">Clicks</span>
                        </div>
                        <p className="text-xl font-semibold">{offer.stats.clicked}</p>
                      </div>
                      <div className="bg-accent/50 p-3 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle2 className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground font-medium">Conversiones</span>
                        </div>
                        <p className="text-xl font-semibold">{offer.stats.converted}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      {offer.status === 'draft' && (
                        <Button 
                          size="sm" 
                          className="bg-primary hover:bg-primary/90 flex-1"
                          onClick={() => handleLaunchCampaign(offer.id, offer.name)}
                          disabled={launchingId === offer.id}
                        >
                          {launchingId === offer.id ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Lanzando...
                            </>
                          ) : (
                            <>
                              <Send className="mr-2 h-4 w-4" />
                              Lanzar Campaña
                            </>
                          )}
                        </Button>
                      )}
                      {offer.status === 'active' && (
                        <Button size="sm" variant="outline" className="flex-1">
                          Ver Detalles
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleEditOffer(offer.id)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => openDeleteDialog(offer.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4 mt-6">
          <Card className="relative overflow-hidden border-border">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
            <CardHeader>
              <CardTitle>Historial de Campañas</CardTitle>
              <CardDescription>
                Campañas ejecutadas y en progreso
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {campaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                        campaign.status === 'completed'
                          ? 'bg-green-500/10 text-green-600'
                          : 'bg-blue-500/10 text-blue-600'
                      }`}>
                        {campaign.status === 'completed' ? (
                          <CheckCircle2 className="h-5 w-5" />
                        ) : (
                          <Send className="h-5 w-5" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{campaign.offerName}</p>
                        <p className="text-xs text-muted-foreground">
                          {campaign.recipients} destinatarios • {campaign.successful} exitosos • {campaign.failed} fallidos
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge
                        variant={campaign.status === 'completed' ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {campaign.status === 'completed' ? 'Completada' : 'En Progreso'}
                      </Badge>
                      {campaign.completedAt && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {campaign.completedAt}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Oferta</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que quieres eliminar esta oferta? 
              Esta acción no se puede deshacer y se perderán todas las estadísticas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={handleDeleteOffer}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
